import { type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PortfolioFilterType } from '../../../enums/PortfolioFilterType';
import { useDealType } from '../../../hooks';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import { usePortfolioFilter } from '../../../hooks/UsePortfolioFilter';
import type { AppState } from '../../../store';
import {
  setIsPortfolioFiltersChanged,
  setOpenFilterCount,
  updateSelectedFilters,
} from '../../../store/portfolio/PortfolioActions';
import type { PortfolioState } from '../../../store/portfolio/PortfolioReducer';
import type { FilterItemResource, MinMaxFilter } from '../../../types/FilterTypes';
import { defaultRange } from '../../../types/PortfolioFilterResource';
import Accordion from '../../shared/filters/accordion/Accordion';
import MultiSelectDropdown from '../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import RangeDropdown from '../../shared/filters/range-dropdown/RangeDropdown';
import { BuyerDetailsOptions } from './components/BuyerDetailsOptions/BuyerDetailsOptions';
import { CurrentVehicleOptions } from './components/CurrentVehicleOptions/CurrentVehicleOptions';
import { ReplacementVehicleOptions } from './components/ReplacementVehicleOptions/ReplacementVehicleOptions';
import { StatusOptions } from './components/StatusOptions/StatusOptions';

const PortfolioFilter: FC = () => {
  const { getPortfolioView, setSelectedFilter } = usePortfolioFilter();
  const { dealType, openFilterCount } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const dispatch = useDispatch();
  const { getDealTypeLabel } = useDealType();
  const currentView = getPortfolioView(dealType);
  const { isMobileDevice } = useCheckDevice();

  const onOpen = () => {
    const newOpenFilterCount = openFilterCount + 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };

  const onClose = () => {
    const newOpenFilterCount = openFilterCount === 0 ? 0 : openFilterCount - 1;
    dispatch(setOpenFilterCount(newOpenFilterCount));
  };

  const handleLBOSelection = (values: FilterItemResource[]) => {
    let bpsValue: MinMaxFilter<number> = { min: 0, max: 100 };
    let lastUpdated: MinMaxFilter<number> = { min: 0, max: 9999 };
    const selectedFilters = currentView.selectedFilters;
    const newSelectedFilters = { ...selectedFilters, bpsValue, lastUpdated };
    newSelectedFilters.bpsValue = bpsValue;
    newSelectedFilters.lastUpdated = lastUpdated;

    //update others filters
    newSelectedFilters.insights = values;
    dispatch(setIsPortfolioFiltersChanged(true, dealType));
    dispatch(updateSelectedFilters(newSelectedFilters, dealType));
  };

  const onPortfolioInsightsFilterChange = (values: FilterItemResource[]) => {
    let lboSelected = values.find((op) => op.key === 'isLeaseBuyout');
    if (lboSelected) {
      handleLBOSelection(values);
      return;
    } else {
      return setSelectedFilter(PortfolioFilterType.Insights, values, dealType);
    }
  };

  return (
    <div>
      {isMobileDevice && (
        <>
          <MultiSelectDropdown
            placeholder={'Deal Type'}
            options={currentView?.filters.dealTypes.map((dealType) => {
              return { ...dealType, label: getDealTypeLabel(parseInt(dealType.key)) };
            })}
            onChange={(values) => {
              setSelectedFilter(
                PortfolioFilterType.DealType,
                values.map((dealType) => {
                  return { ...dealType, label: dealType.key };
                }),
                dealType,
              );
            }}
            onUpdate={(values) =>
              setSelectedFilter(
                PortfolioFilterType.DealType,
                values.map((dealType) => {
                  return { ...dealType, label: dealType.key };
                }),
                dealType,
                false,
              )
            }
            values={currentView?.selectedFilters.dealTypes}
            allowSearchFilter={false}
            type='am-dropdown'
            selectedLabelPosition={'right'}
            displaySelectedLabels
            onOpen={onOpen}
            onClose={onClose}
            cssClass={isMobileDevice ? 'responsive-adjust' : ''}
            dynamicRender={false}
          />
          <RangeDropdown
            type='am-dropdown'
            placeholder={'BPS'}
            selectedValue={currentView?.selectedFilters.bpsValue}
            possibleRange={defaultRange}
            maxLength={3}
            onChange={(value) => {
              setSelectedFilter(PortfolioFilterType.BpsValue, value, dealType);
            }}
            selectedLabelPosition={'right'}
            cssClass='responsive-adjust'
          />
          <MultiSelectDropdown
            placeholder={'Insights'}
            displaySelectedLabels
            options={currentView?.filters.insights}
            onChange={(values) => onPortfolioInsightsFilterChange(values)}
            onUpdate={(values) =>
              setSelectedFilter(PortfolioFilterType.Insights, values, dealType, false)
            }
            values={currentView?.selectedFilters.insights}
            allowSearchFilter={false}
            enableCheckAll={false}
            onOpen={onOpen}
            onClose={onClose}
            type='am-dropdown'
            cssClass={isMobileDevice ? 'responsive-adjust' : ''}
            dynamicRender={false}
          />
        </>
      )}
      <Accordion isOpen placeholder='Status' cssClass='no-margin' padding={2}>
        <StatusOptions onOpen={onOpen} onClose={onClose} />
      </Accordion>
      <Accordion isOpen placeholder='Buyer Details' padding={2}>
        <BuyerDetailsOptions onOpen={onOpen} onClose={onClose} />
      </Accordion>
      <Accordion isOpen={false} placeholder='Current Vehicle' padding={2}>
        <CurrentVehicleOptions onOpen={onOpen} onClose={onClose} />
      </Accordion>
      <Accordion isOpen={false} placeholder='Replacement Vehicle' padding={2}>
        <ReplacementVehicleOptions onOpen={onOpen} onClose={onClose} />
      </Accordion>
    </div>
  );
};

export default PortfolioFilter;
